import React, { useCallback, useMemo, useState } from 'react'

import KanbanColumnStatesContext from 'contexts/KanbanColumnStatesContext'

import useKanbanList from 'hooks/useKanbanList'
import useKanbanBoard from 'hooks/useKanbanBoard'
import useKanbanColumn from 'hooks/useKanbanColumn'
import useKanbanFilters from 'hooks/useKanbanFilters'
import useKanbanDragDrop from 'hooks/useKanbanDragDrop'
import constants from 'constants/index'

const KanbanColumnStatesProvider = ({ children }) => {
  const { KANBAN_COLUMN_STATES_ORDER, VIEW_MODE_KANBAN } = constants.activities

  const [refreshKey, setRefreshKey] = useState(0)

  const { columns, setColumns, boardId, canFinalize } = useKanbanBoard()
  const { filter, onlyMine, searchInput, viewMode } = useKanbanFilters()
  const { handleDeleteCard } = useKanbanList()

  const refetchColumnStates = useCallback(() => {
    setRefreshKey((previousKey) => previousKey + 1)
  }, [])

  const kanbanFetchParams = useMemo(
    () => ({
      order: KANBAN_COLUMN_STATES_ORDER,
      ...filter.filters,
      ...(onlyMine ? { mine: true } : {}),
      ...(searchInput ? { name: searchInput } : {}),
    }),
    [filter.filters, onlyMine, searchInput], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const {
    columnStates,
    setColumnStates,
    handleLoadMore,
    handleLoadMoreAll,
    handleDeleteCard: handleDeleteKanbanCard,
  } = useKanbanColumn({
    columns,
    fetchParams: kanbanFetchParams,
    refreshKey,
    active: viewMode === VIEW_MODE_KANBAN,
    onDeleteCard: handleDeleteCard,
  })

  const updateTaskInColumn = useCallback(
    (task) => {
      const newColId = String(task.kanbanColumnId)
      setColumnStates((prev) => {
        const oldColId = Object.keys(prev).find((cid) =>
          prev[cid].tasks.some((t) => t.id === task.id),
        )
        if (!oldColId) return prev

        const sameColumn = oldColId === newColId
        if (sameColumn) {
          return {
            ...prev,
            [oldColId]: {
              ...prev[oldColId],
              tasks: prev[oldColId].tasks.map((t) =>
                t.id === task.id ? task : t,
              ),
            },
          }
        }

        const oldCol = prev[oldColId]
        const newCol = prev[newColId]
        if (!newCol) return prev

        return {
          ...prev,
          [oldColId]: {
            ...oldCol,
            tasks: oldCol.tasks.filter((t) => t.id !== task.id),
            totalCount: Math.max(0, oldCol.totalCount - 1),
          },
          [newColId]: {
            ...newCol,
            tasks: [task, ...newCol.tasks],
            totalCount: newCol.totalCount + 1,
          },
        }
      })
    },
    [setColumnStates],
  )

  const addTaskToColumn = useCallback(
    (task) => {
      const colId = String(task.kanbanColumnId)
      setColumnStates((prev) => {
        const col = prev[colId]
        if (!col) return prev
        return {
          ...prev,
          [colId]: {
            ...col,
            tasks: [task, ...col.tasks],
            totalCount: col.totalCount + 1,
          },
        }
      })
    },
    [setColumnStates],
  )

  const { handleDragEnd, pendingMove, confirmPendingMove, cancelPendingMove } =
    useKanbanDragDrop(
      columns,
      setColumns,
      columnStates,
      setColumnStates,
      boardId,
      canFinalize,
    )

  return (
    <KanbanColumnStatesContext.Provider
      value={{
        columnStates,
        handleLoadMore,
        handleLoadMoreAll,
        handleDeleteKanbanCard,
        handleDragEnd,
        pendingMove,
        confirmPendingMove,
        cancelPendingMove,
        addTaskToColumn,
        updateTaskInColumn,
        refetchColumnStates,
      }}
    >
      {children}
    </KanbanColumnStatesContext.Provider>
  )
}

export default KanbanColumnStatesProvider
